import { Injectable, computed, signal, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ViewWidget } from '../../../interfaces/view-widget';
import { WIDGET_LIMITS, WIDGET_DIMENSIONS, WIDGET_PRIORITY, THRESHOLDS, FALLBACKS } from '../constants';

export interface WidgetDisplayConfig {
  maxGroupWidgets?: number;
  defaultMaxWidgets?: number;
  enablePriority?: boolean;
  enableFiltering?: boolean;
  estimatedWidgetHeight?: number;
  minWidgetHeight?: number;
  containerPadding?: number;
}

export interface WidgetWithPriority extends ViewWidget {
  priority?: number;
  visible?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class WidgetsDisplayService {
  private readonly document = inject(DOCUMENT);

  private readonly config = signal<WidgetDisplayConfig>({
    maxGroupWidgets: WIDGET_LIMITS.MAX_GROUP_WIDGETS,
    defaultMaxWidgets: WIDGET_LIMITS.DEFAULT_MAX_WIDGETS,
    enablePriority: true,
    enableFiltering: true,
    estimatedWidgetHeight: WIDGET_DIMENSIONS.ESTIMATED_HEIGHT,
    minWidgetHeight: WIDGET_DIMENSIONS.MIN_HEIGHT,
    containerPadding: WIDGET_DIMENSIONS.CONTAINER_PADDING,
  });

  // computed values for getting configuration
  readonly maxGroupWidgets = computed(() => this.config().maxGroupWidgets ?? WIDGET_LIMITS.MAX_GROUP_WIDGETS);
  readonly defaultMaxWidgets = computed(() => this.config().defaultMaxWidgets ?? WIDGET_LIMITS.DEFAULT_MAX_WIDGETS);
  readonly estimatedWidgetHeight = computed(() => this.config().estimatedWidgetHeight ?? FALLBACKS.WIDGET_HEIGHT);
  readonly containerPadding = computed(() => this.config().containerPadding ?? FALLBACKS.CONTAINER_PADDING);

  updateConfig(config: Partial<WidgetDisplayConfig>): void {
    this.config.update(current => ({ ...current, ...config }));
  }

  prepareListWidgets(widgets: ViewWidget[], maxItems?: number): ViewWidget[] {
    const max = maxItems ?? widgets.length;
    return this.processWidgets(widgets, max);
  }

  // prepare group widgets
  prepareGroupWidgets(widgets: ViewWidget[], maxItems?: number): ViewWidget[] {
    const max = maxItems ?? this.maxGroupWidgets();
    return this.processWidgets(widgets, max);
  }

  private processWidgets(widgets: ViewWidget[], maxItems: number): ViewWidget[] {
    if (!widgets || widgets.length === THRESHOLDS.EMPTY_COUNT) {
      return [];
    }

    let processedWidgets = [...widgets];

    // filter only visible widgets if filtering is enabled
    if (this.config().enableFiltering) {
      processedWidgets = processedWidgets.filter(widget => (widget as WidgetWithPriority).visible !== false);
    }

    // sort by priority if priority is enabled
    if (this.config().enablePriority) {
      processedWidgets.sort((a, b) => {
        const priorityA = (a as WidgetWithPriority).priority ?? WIDGET_PRIORITY.DEFAULT;
        const priorityB = (b as WidgetWithPriority).priority ?? WIDGET_PRIORITY.DEFAULT;
        return priorityA - priorityB;
      });
    }

    return processedWidgets.slice(THRESHOLDS.MIN_WIDGET_COUNT, maxItems);
  }

  // check if can display widgets
  canDisplayWidgets(count: number, type: 'list' | 'group' = 'list'): boolean {
    // remove limit for lists, keep only for groups
    if (type === 'list') {
      return true;
    }
    const maxAllowed = this.maxGroupWidgets();
    return count <= maxAllowed;
  }

  paginateWidgets(widgets: ViewWidget[], pageSize: number, pageIndex: number = 0): ViewWidget[] {
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    return widgets.slice(startIndex, endIndex);
  }

  // group widgets by type or category
  groupWidgetsByType<T extends ViewWidget & { type?: string }>(widgets: T[]): Map<string, T[]> {
    const grouped = new Map<string, T[]>();

    widgets.forEach(widget => {
      const type = widget.type || 'default';
      const existing = grouped.get(type) || [];
      grouped.set(type, [...existing, widget]);
    });

    return grouped;
  }

  // check if all widgets can be displayed
  hasMoreWidgets(widgets: ViewWidget[], displayedCount: number): boolean {
    return widgets.length > displayedCount;
  }

  // calculate number of widgets that can fit in viewport
  calculateVisibleWidgetsByViewport(containerElement?: HTMLElement): number {
    const viewportHeight = this.getViewportHeight();
    const containerHeight = containerElement?.offsetHeight ?? viewportHeight;
    const availableHeight = Math.min(viewportHeight, containerHeight) - this.containerPadding();

    if (availableHeight <= THRESHOLDS.MIN_AVAILABLE_HEIGHT) {
      return THRESHOLDS.MIN_WIDGET_COUNT;
    }

    const estimatedHeight = this.estimatedWidgetHeight();
    const maxWidgets = Math.floor(availableHeight / estimatedHeight);

    return Math.max(THRESHOLDS.MIN_WIDGET_COUNT, maxWidgets);
  }

  // calculate number of widgets by available height
  calculateWidgetsByHeight(availableHeight: number, gap: number = WIDGET_DIMENSIONS.DEFAULT_GAP): number {
    if (availableHeight <= THRESHOLDS.MIN_AVAILABLE_HEIGHT) {
      return THRESHOLDS.MIN_WIDGET_COUNT;
    }

    const padding = this.containerPadding();
    const effectiveHeight = availableHeight - padding;

    if (effectiveHeight <= THRESHOLDS.MIN_AVAILABLE_HEIGHT) {
      return THRESHOLDS.MIN_WIDGET_COUNT;
    }

    const widgetHeight = this.estimatedWidgetHeight();
    const minWidgetHeight = this.config().minWidgetHeight ?? WIDGET_DIMENSIONS.MIN_HEIGHT;
    const safeWidgetHeight = Math.max(widgetHeight, minWidgetHeight);

    // first widget doesn't have gap above
    const firstWidgetSpace = safeWidgetHeight;
    if (effectiveHeight < firstWidgetSpace) {
      return THRESHOLDS.MIN_WIDGET_COUNT;
    }

    // for other widgets we consider gap
    const remainingHeight = effectiveHeight - firstWidgetSpace;
    const subsequentWidgetSpace = safeWidgetHeight + gap;

    if (remainingHeight <= THRESHOLDS.MIN_AVAILABLE_HEIGHT) {
      return THRESHOLDS.SINGLE_WIDGET;
    }

    const additionalWidgets = Math.floor(remainingHeight / subsequentWidgetSpace);

    const totalWidgets = THRESHOLDS.SINGLE_WIDGET + additionalWidgets;

    return totalWidgets;
  }

  private getViewportHeight(): number {
    return this.document.defaultView?.innerHeight ?? FALLBACKS.VIEWPORT_HEIGHT;
  }

  isContainerVisible(containerElement: HTMLElement): boolean {
    const rect = containerElement.getBoundingClientRect();
    const viewportHeight = this.getViewportHeight();

    // check if at least part of the container is visible
    return rect.top < viewportHeight && rect.bottom > THRESHOLDS.MIN_AVAILABLE_HEIGHT;
  }

  prepareAdaptiveWidgets(
    widgets: ViewWidget[],
    containerElement?: HTMLElement,
    type: 'list' | 'group' = 'list'
  ): ViewWidget[] {
    if (!widgets || widgets.length === THRESHOLDS.EMPTY_COUNT) {
      return [];
    }

    // calculate maximum number of widgets by viewport
    const maxByViewport = this.calculateVisibleWidgetsByViewport(containerElement);

    const maxByType = type === 'list' ? widgets.length : this.maxGroupWidgets();

    const maxItems = Math.min(maxByViewport, maxByType);

    // if widgets don't fit, return empty array
    if (maxItems <= THRESHOLDS.MIN_WIDGET_COUNT) {
      return [];
    }

    return this.processWidgets(widgets, maxItems);
  }
}
